import type { MouseEventHandler } from 'react';
import { sprintf } from 'sprintf-js';

import type { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import type { TileGridVariant } from 'components/TileGrid/TileGridVariant';
import { useTabContext } from 'containers/TabContext';

import {
  TileGridBlockTileHeading,
  TileGridBlockTileImageHolder,
  TileGridBlockTileLink,
  TileGridBlockTileSubHeading,
} from './TileGridTile.styled';
import { tileGridTilePropsByVariant } from './tileGridTilePropsByVariant';

type Props = {
  id: string;
  imageData: ApiFilestackImageData;
  variant: TileGridVariant;
  heading: string;
  subHeading: string | null;
  description: string;
  tileHref: string;
  onClick: MouseEventHandler;
};

export function TileGridTile({
  id,
  imageData,
  variant,
  heading,
  subHeading,
  description,
  tileHref,
  onClick,
}: Props) {
  const alt = sprintf(getText('Image of %(heading)s'), { heading });

  const { width, height, borderRadius, qaId, marginBottom, noCrop } =
    tileGridTilePropsByVariant[variant];

  const oneByOneRatio = width === height;
  const isTabbing = useTabContext();

  return (
    <TileGridBlockTileLink
      id={id}
      href={tileHref}
      $isTabbing={isTabbing}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      <TileGridBlockTileImageHolder
        style={{
          marginBottom,
          aspectRatio: oneByOneRatio ? '1' : undefined,
          borderRadius,
          width,
          height: oneByOneRatio ? undefined : height,
        }}
      >
        <FilestackImage
          data={imageData}
          data-qa-id={qaId}
          alt={alt}
          noCrop={noCrop}
          width={width}
          height={height}
          objectFit="cover"
        />
      </TileGridBlockTileImageHolder>

      <TileGridBlockTileHeading>{heading}</TileGridBlockTileHeading>

      {subHeading && (
        <TileGridBlockTileSubHeading>{subHeading}</TileGridBlockTileSubHeading>
      )}

      <div
        style={{ display: 'none' }}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </TileGridBlockTileLink>
  );
}
